/**
 * NBA Rates — NBA Player Impact Tool
 *
 * IMPORTANT: This tool uses the omaha-design-system library.
 * omaha-design-system is a MUI wrapper and should ONLY be used in this tool
 * and the Stokastic Props tool to avoid conflicts with the existing
 * @material-ui and @mui dependencies used elsewhere in the application.
 *
 * DO NOT import from omaha-design-system in any other tool.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { ThemeProvider, darkTheme, Box, Typography, Stack, CircularProgress } from 'omaha-design-system';
import { useAuth } from '../../hooks/useAuth';
import ToolsHeader from '../common/ToolsHeader';
import { UnlockOverlay } from './UnlockOverlay';
import { Helmet } from 'react-helmet';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Authentication from '../common/Authentication';
import NbaRatesFilterBuilder from './NbaRatesFilterBuilder';
import NbaRatesOptionalFilters from './NbaRatesOptionalFilters';
import NbaRatesStatsTable from './NbaRatesStatsTable';
import { useNbaRatesFilters, useTeamPlayers, useNbaRatesStats } from './hooks';
import type { OptionalFilters, FilterPool, NbaRatesPlayer } from './types';
import {
  NBA_TEAMS,
  SEASONS,
  SEASON_TYPES,
  CONDITIONS,
  COMPARISONS,
  DEFAULT_OPTIONAL_FILTERS,
  LEVERAGE_OPTIONS,
  PERIOD_OPTIONS,
  getLastName,
} from './types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

// ─── Inner Content (needs QueryClient context) ──────────────────────────────

const NbaRatesContent: React.FC = () => {
  // Core selections
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>(['2025-26']);
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  const [seasonType, setSeasonType] = useState('Regular Season');

  // Optional filters
  const [optionalFilters, setOptionalFilters] = useState<OptionalFilters>(
    DEFAULT_OPTIONAL_FILTERS,
  );

  // Filter pools via reducer
  const { filterPools, dispatch } = useNbaRatesFilters();

  // Config panel collapse
  const [configCollapsed, setConfigCollapsed] = useState(false);

  // React Query hooks
  const seasonsParam = selectedSeasons.join(',');

  const {
    data: teamPlayersData,
    isLoading: loadingPlayers,
  } = useTeamPlayers(selectedTeamId, seasonsParam);

  const {
    data: nbaRatesData,
    isFetching: loadingStats,
    error: statsError,
    refetch: fetchStats,
  } = useNbaRatesStats({
    teamId: selectedTeamId,
    seasons: seasonsParam,
    seasonType,
    filterPools,
    optionalFilters,
    includeBaseline: true,
  });

  const players = teamPlayersData?.players ?? [];
  const selectedTeam = NBA_TEAMS.find((t) => t.id === selectedTeamId);

  /** Build human-readable filter descriptions for the detail card */
  const filterTerms = useMemo(() => {
    const terms: string[] = [];

    // Player filter pools
    filterPools.forEach((pool) => {
      if (pool.playerIds.length === 0) return;
      const cond = CONDITIONS.find((c) => c.value === pool.condition);
      const comp = COMPARISONS.find((c) => c.value === pool.comparison);
      const names = pool.playerIds
        .map((id) => {
          const p = players.find((pl) => pl.player_id === id);
          return p ? getLastName(p.player_name) : id;
        })
        .join(', ');
      terms.push(`${cond?.label ?? pool.condition} ${comp?.symbol ?? '='}${pool.count}: ${names}`);
    });

    // Optional filters
    if (optionalFilters.leverage.length > 0) {
      terms.push(`Leverage: ${optionalFilters.leverage.join(', ')}`);
    }
    if (optionalFilters.periods.length > 0) {
      const periodLabels = optionalFilters.periods
        .map((p) => PERIOD_OPTIONS.find((opt) => opt.value === p)?.label ?? `Q${p}`)
        .join(', ');
      terms.push(`Period: ${periodLabels}`);
    }
    if (optionalFilters.fromMargin != null || optionalFilters.toMargin != null) {
      const from = optionalFilters.fromMargin ?? '...';
      const to = optionalFilters.toMargin ?? '...';
      terms.push(`Margin: ${from} to ${to}`);
    }
    if (optionalFilters.fromDate || optionalFilters.toDate) {
      const from = optionalFilters.fromDate ?? '...';
      const to = optionalFilters.toDate ?? '...';
      terms.push(`${from} \u2013 ${to}`);
    }
    if (optionalFilters.opponent) {
      const opp = NBA_TEAMS.find((t) => t.id === optionalFilters.opponent);
      terms.push(`vs ${opp?.abbreviation ?? optionalFilters.opponent}`);
    }

    return terms;
  }, [filterPools, optionalFilters, players]);

  // Handlers
  const handleTeamChange = useCallback(
    (teamId: string) => {
      setSelectedTeamId(teamId);
      setSelectedSeasons(['2025-26']);
      dispatch({ type: 'RESET' });
      setOptionalFilters(DEFAULT_OPTIONAL_FILTERS);
    },
    [dispatch],
  );

  const toggleSeason = useCallback(
    (season: string) => {
      setSelectedSeasons((prev) => {
        if (prev.includes(season)) {
          // Prevent deselecting the last season
          if (prev.length === 1) return prev;
          return prev.filter((s) => s !== season);
        }
        return [...prev, season];
      });
      dispatch({ type: 'RESET' });
    },
    [dispatch],
  );

  const handleFetchStats = useCallback(() => {
    if (!selectedTeamId) return;
    fetchStats();
    setConfigCollapsed(true);
  }, [selectedTeamId, fetchStats]);

  const errorMessage =
    statsError instanceof Error ? statsError.message : statsError ? String(statsError) : null;

  return (
    <Box
      sx={{
        maxWidth: 1600,
        mx: 'auto',
        p: 1.5,
      }}
    >
      {/* Configuration Card */}
      <Box
        sx={{
          bgcolor: '#0D0F13',
          borderRadius: '10px',
          border: '1px solid #1E2330',
        }}
      >
        {/* Collapsible Header */}
        <Box
          component="button"
          onClick={() => setConfigCollapsed(!configCollapsed)}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '0.625rem 1rem',
            bgcolor: 'transparent',
            border: 'none',
            borderBottom: '1px solid #1E2330',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                color: '#8B95A5',
                fontSize: '0.75rem',
                transition: 'transform 0.2s',
                transform: configCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              }}
            >
              {'\u25B6'}
            </Typography>
            {configCollapsed ? (
              <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                <Typography sx={{ color: '#E8EAED', fontWeight: 600 }}>
                  {selectedTeam?.abbreviation || 'No Team'}
                </Typography>
                <Typography sx={{ color: '#3A3F4B' }}>|</Typography>
                <Typography sx={{ color: '#8B95A5' }}>{selectedSeasons.join(', ')}</Typography>
                {filterPools.filter((p) => p.playerIds.length > 0).length > 0 && (
                  <>
                    <Typography sx={{ color: '#3A3F4B' }}>|</Typography>
                    {filterPools
                      .filter((p) => p.playerIds.length > 0)
                      .map((pool) => {
                        const cond = CONDITIONS.find((c) => c.value === pool.condition);
                        const comp = COMPARISONS.find((c) => c.value === pool.comparison);
                        const names = pool.playerIds
                          .map((id) => {
                            const player = players.find((p) => p.player_id === id);
                            return player ? getLastName(player.player_name) : id;
                          })
                          .join(', ');
                        return (
                          <Box
                            key={pool.id}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.25,
                              py: 0.25,
                              bgcolor: 'rgba(255,255,255,0.04)',
                              borderRadius: '6px',
                              border: '1px solid #1E2330',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: cond?.color ?? '#8B95A5',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                              }}
                            >
                              {cond?.label ?? pool.condition}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#6B7280' }}>
                              {comp?.symbol ?? '='}{pool.count}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#C9CDD4' }}>
                              {names}
                            </Typography>
                          </Box>
                        );
                      })}
                  </>
                )}
              </Stack>
            ) : (
              <Box>
                <Typography sx={{ color: '#E8EAED', fontWeight: 600 }}>
                  Configuration
                </Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>
                  Select team and set player filters
                </Typography>
              </Box>
            )}
          </Stack>
          {configCollapsed && (
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setConfigCollapsed(false);
              }}
              sx={{
                px: 2,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 500,
                bgcolor: '#252830',
                color: '#8B95A5',
                borderRadius: 1,
                '&:hover': { bgcolor: '#2A2F3A' },
                transition: 'background 0.15s',
              }}
            >
              Edit
            </Box>
          )}
        </Box>

        {/* Expandable Content */}
        {!configCollapsed && (
          <Box sx={{ p: 1.5 }}>
            {/* Team / Season / Type Selection + Fetch Button */}
            <Stack
              direction="row"
              sx={{ flexWrap: 'wrap', gap: 1.5, mb: 1.5, alignItems: 'flex-end' }}
            >
              {/* Team */}
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#8B95A5', mb: 0.5, fontSize: '0.8rem' }}
                >
                  Team
                </Typography>
                <Box
                  component="select"
                  value={selectedTeamId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleTeamChange(e.target.value)
                  }
                  sx={{
                    width: '100%',
                    px: 1.25,
                    py: 1,
                    fontSize: '0.9rem',
                    border: '1px solid #1E2330',
                    borderRadius: 1,
                    bgcolor: '#1E2330',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                >
                  <option value="">Select Team</option>
                  {NBA_TEAMS.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Box>
              </Box>

              {/* Season (multi-select dropdown) */}
              <Box sx={{ flex: 1, minWidth: 180, position: 'relative' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#8B95A5', mb: 0.5, fontSize: '0.8rem' }}
                >
                  Season
                </Typography>
                <Box
                  component="button"
                  onClick={() => setSeasonDropdownOpen(!seasonDropdownOpen)}
                  sx={{
                    width: '100%',
                    px: 1.25,
                    py: 1,
                    fontSize: '0.9rem',
                    border: '1px solid',
                    borderColor: seasonDropdownOpen ? '#3B82F6' : '#1E2330',
                    borderRadius: 1,
                    bgcolor: '#1E2330',
                    color: '#E8EAED',
                    outline: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedSeasons.join(', ')}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: '0.6rem',
                      color: '#8B95A5',
                      transition: 'transform 0.2s',
                      transform: seasonDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    {'\u25BC'}
                  </Box>
                </Box>
                {seasonDropdownOpen && (
                  <>
                    <Box
                      onClick={() => setSeasonDropdownOpen(false)}
                      sx={{ position: 'fixed', inset: 0, zIndex: 9 }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        mt: 0.5,
                        bgcolor: '#1E2330',
                        border: '1px solid #2A2F3A',
                        borderRadius: 1,
                        zIndex: 10,
                        maxHeight: 280,
                        overflow: 'auto',
                        py: 0.5,
                      }}
                    >
                      {SEASONS.map((s) => (
                        <Box
                          key={s.value}
                          component="button"
                          onClick={() => toggleSeason(s.value)}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            py: 0.75,
                            bgcolor: 'transparent',
                            border: 'none',
                            color: '#E8EAED',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            '&:hover': { bgcolor: '#252830' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '3px',
                              border: '1px solid',
                              borderColor: selectedSeasons.includes(s.value) ? '#3B82F6' : '#6B7280',
                              bgcolor: selectedSeasons.includes(s.value) ? '#3B82F6' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              color: 'white',
                              flexShrink: 0,
                            }}
                          >
                            {selectedSeasons.includes(s.value) && '\u2713'}
                          </Box>
                          {s.label}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>

              {/* Season Type */}
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#8B95A5', mb: 0.5, fontSize: '0.8rem' }}
                >
                  Season Type
                </Typography>
                <Box
                  component="select"
                  value={seasonType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSeasonType(e.target.value)
                  }
                  sx={{
                    width: '100%',
                    px: 1.25,
                    py: 1,
                    fontSize: '0.9rem',
                    border: '1px solid #1E2330',
                    borderRadius: 1,
                    bgcolor: '#1E2330',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                >
                  {SEASON_TYPES.map((st) => (
                    <option key={st.value} value={st.value}>
                      {st.label}
                    </option>
                  ))}
                </Box>
              </Box>

              {/* Fetch Button */}
              <Box
                component="button"
                onClick={handleFetchStats}
                disabled={!selectedTeamId || loadingStats}
                sx={{
                  px: 3,
                  py: 1.125,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  bgcolor: !selectedTeamId || loadingStats ? '#1E2330' : '#10B981',
                  color: !selectedTeamId || loadingStats ? '#6B7280' : '#000',
                  border: 'none',
                  borderRadius: 1,
                  cursor: !selectedTeamId || loadingStats ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s',
                  '&:hover': {
                    bgcolor:
                      !selectedTeamId || loadingStats ? '#1E2330' : '#059669',
                  },
                }}
              >
                {loadingStats ? 'Loading...' : 'Fetch Stats'}
              </Box>
            </Stack>

            {/* Player Filters */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: '#8B95A5', mb: 1 }}
              >
                Player Filters
              </Typography>

              {!selectedTeamId && (
                <Typography
                  sx={{ fontSize: '0.875rem', color: '#6B7280', fontStyle: 'italic' }}
                >
                  Select a team to see available players
                </Typography>
              )}

              {loadingPlayers && (
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Loading players...
                </Typography>
              )}

              {selectedTeamId && !loadingPlayers && players.length > 0 && (
                <NbaRatesFilterBuilder
                  filterPools={filterPools}
                  players={players}
                  dispatch={dispatch}
                />
              )}

              {/* Optional Filters */}
              {selectedTeamId && (
                <NbaRatesOptionalFilters
                  filters={optionalFilters}
                  currentTeamId={selectedTeamId}
                  onChange={setOptionalFilters}
                />
              )}
            </Box>

            {/* Error */}
            {errorMessage && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 1,
                  color: '#f87171',
                  fontSize: '0.875rem',
                }}
              >
                {errorMessage}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Loading Indicator */}
      {loadingStats && !nbaRatesData && (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ mt: 6, mb: 6 }}>
          <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
          <Typography sx={{ color: '#8B95A5', fontSize: '0.875rem' }}>
            Fetching player stats...
          </Typography>
        </Stack>
      )}

      {/* Results Table */}
      {nbaRatesData && (
        <Box sx={{ mt: 1.5 }}>
          <NbaRatesStatsTable
            data={nbaRatesData}
            teamName={selectedTeam?.name || ''}
            teamId={selectedTeamId}
            filterTerms={filterTerms}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Root Component with Providers ──────────────────────────────────────────

const NbaRates: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <ThemeProvider theme={darkTheme}>
      <QueryClientProvider client={queryClient}>
        <Authentication app="NBA_RATES">
          <Stack
            sx={{
              fontFamily: 'Inter',
              bgcolor: '#0A0A0A',
              flexGrow: 1,
              height: '100vh',
              overflowX: 'hidden',
              // Global scrollbar styling
              '& *::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '& *::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '& *::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
              },
              '& *::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.35)',
              },
              '& *': {
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255, 255, 255, 0.15) transparent',
              },
            }}
          >
            <Helmet>
              <title>NBA Rates - Player Impact Analysis</title>
              <meta
                name="description"
                content="NBA Rates player impact analysis tool"
              />
            </Helmet>
            <ToolsHeader />
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
              </Stack>
            ) : !isAuthenticated ? (
              <UnlockOverlay />
            ) : (
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <NbaRatesContent />
              </Box>
            )}
          </Stack>
        </Authentication>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default NbaRates;
